import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { jobsApi } from '@/api/jobs';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';

/** The jobs screen: every job, or those in one state, newest first. Polled: jobs move. */
export function useJobs(state: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.jobs.list(state),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => jobsApi.list({ ...(state ? { state } : {}), limit: 50, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 5000,
  });
}

/** The dashboard's handful of newest jobs. */
export function useRecentJobs() {
  return useQuery({ queryKey: queryKeys.jobs.recent, queryFn: () => jobsApi.list({ limit: 6 }), refetchInterval: 10000 });
}

/**
 * A site's newest jobs, for what its page has to know now: a removal or a move
 * in progress, a creation that failed.
 */
export function useSiteJobs(siteId: string) {
  return useQuery({
    queryKey: queryKeys.jobs.site(siteId),
    queryFn: () => jobsApi.list({ siteId, limit: 50 }),
    enabled: siteId !== '',
    refetchInterval: 5000,
  });
}

/** A site's whole history, paged, for its History tab. */
export function useSiteJobHistory(siteId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.jobs.siteHistory(siteId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => jobsApi.list({ siteId, limit: 50, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 5000,
  });
}

export function useCancelJob(callbacks?: MutationCallbacks<{ id: string; state: string }, string>) {
  return useApiMutation(jobsApi.cancel, () => [queryKeys.jobs.all], callbacks);
}
